import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useLoanOraclePriceQuotes } from "app/components/common";
import { TransactionTypes } from "app/types";
import { StrategyDuration } from "@bridgesplit/abf-sdk";
import { AbfLoanExpanded, getZcCollateral, getZcLedger } from "@bridgesplit/abf-react";
import { bsMath, roundToDecimals } from "@bridgesplit/utils";

import {
    useAddCollateral,
    useIncreasePrincipal,
    useSubmitIncreasePrincipal,
    useManualIncreaseLeverage,
    useAutofillClampNumber,
    useTouchGateSetter
} from "./payments/hooks";

/**
 * Principal to borrow (UI units) so after deposit + full swap of borrowed principal into collateral,
 * LTV matches the pre-trade position (same USD model as pending leverage in ManualIncreaseLeverage).
 */
function borrowPrincipalToMaintainLtv({
    currentPrincipalDebt,
    currentCollateralAmount,
    depositCollateralAmount,
    principalUsdOraclePrice,
    collateralUsdOraclePrice,
    principalDecimals
}: {
    currentPrincipalDebt: number;
    currentCollateralAmount: number;
    depositCollateralAmount: number;
    principalUsdOraclePrice: number | undefined;
    collateralUsdOraclePrice: number | undefined;
    principalDecimals: number | undefined;
}): number | undefined {
    if (!Number.isFinite(depositCollateralAmount) || depositCollateralAmount <= 0) {
        return 0;
    }
    if (
        !Number.isFinite(currentPrincipalDebt) ||
        currentPrincipalDebt < 0 ||
        principalUsdOraclePrice == null ||
        collateralUsdOraclePrice == null ||
        !Number.isFinite(principalUsdOraclePrice) ||
        !Number.isFinite(collateralUsdOraclePrice) ||
        principalUsdOraclePrice <= 0 ||
        collateralUsdOraclePrice <= 0 ||
        principalDecimals == null
    ) {
        return undefined;
    }

    const debtUsd = bsMath.mul(currentPrincipalDebt, principalUsdOraclePrice);
    const collateralUsd = bsMath.mul(currentCollateralAmount, collateralUsdOraclePrice);
    const depositUsd = bsMath.mul(depositCollateralAmount, collateralUsdOraclePrice);
    const equityUsd = bsMath.sub(collateralUsd, debtUsd);
    if (equityUsd == null || !Number.isFinite(equityUsd) || equityUsd <= 0) return undefined;

    const raw = bsMath.div(bsMath.mul(currentPrincipalDebt, depositUsd), equityUsd);
    if (!Number.isFinite(raw) || raw < 0) return undefined;
    return roundToDecimals(raw, principalDecimals);
}

export function useManualIncreaseLeverageController({
    loanExpanded,
    strategyDuration
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    strategyDuration?: StrategyDuration;
}) {
    const [additionalPrincipal, _setAdditionalPrincipal] = useState<number | undefined>(undefined);
    const [hasTouchedCollateral, setHasTouchedCollateral] = useState(false);
    const shouldMaxBorrowAfterCollateralMaxRef = useRef(false);

    const {
        inputAmount: collateralInputAmount,
        setInputAmount: _setCollateralInputAmount,
        collateralMetadata,
        balance: collateralBalance,
        balanceLoading,
        insufficientBalance,
        permissionDenied,
        permissionMessage,
        submit: handleAddCollateralSubmit
    } = useAddCollateral({
        loanExpanded
    });

    const setCollateralInputAmount = useTouchGateSetter(setHasTouchedCollateral, _setCollateralInputAmount, {
        onlyWhen: (v) => v !== undefined
    });

    const {
        option,
        validOptions,
        isRefinance,
        isLoadingBestQuotes,
        additionalPrincipalLimit,
        maxBorrowAlert,
        isLoadingMaxBorrowLimit,
        isFetchingMaxBorrowLimit
    } = useIncreasePrincipal({
        loanExpanded,
        strategyDurations: strategyDuration ? [strategyDuration] : undefined,
        additionalPrincipal,
        additionalCollateral: collateralInputAmount
    });

    const zcCollateral = loanExpanded ? getZcCollateral(loanExpanded) : undefined;
    const zcLedger = loanExpanded ? getZcLedger(loanExpanded) : undefined;
    const positionPrincipalDebt = zcLedger?.ledgerDebt.total ?? 0;
    const positionCollateralAmount = zcCollateral?.amount ?? 0;
    const { principalUsdOraclePrice, collateralUsdOraclePrice } = useLoanOraclePriceQuotes(
        loanExpanded?.principalMetadata,
        zcCollateral
    );

    const borrowAutofillMaintainLtv = useMemo(
        () =>
            borrowPrincipalToMaintainLtv({
                currentPrincipalDebt: positionPrincipalDebt,
                currentCollateralAmount: positionCollateralAmount,
                depositCollateralAmount: collateralInputAmount ?? 0,
                principalUsdOraclePrice,
                collateralUsdOraclePrice,
                principalDecimals: loanExpanded?.principalMetadata?.decimals
            }),
        [
            positionPrincipalDebt,
            positionCollateralAmount,
            collateralInputAmount,
            principalUsdOraclePrice,
            collateralUsdOraclePrice,
            loanExpanded?.principalMetadata?.decimals
        ]
    );

    const borrowField = useAutofillClampNumber({
        max: additionalPrincipalLimit?.additionalBorrowLimit,
        enabled: !!loanExpanded,
        gateAutofill: hasTouchedCollateral,
        onValueChange: _setAdditionalPrincipal,
        autofillTarget: borrowAutofillMaintainLtv
    });
    const availableBorrowLimit = additionalPrincipalLimit?.additionalBorrowLimit;

    useEffect(() => {
        if (!shouldMaxBorrowAfterCollateralMaxRef.current) return;
        if (isLoadingMaxBorrowLimit || isFetchingMaxBorrowLimit) return;
        if (availableBorrowLimit === undefined) return;

        if (!borrowField.isUserEdited) {
            borrowField.setValueRaw(availableBorrowLimit);
        }
        shouldMaxBorrowAfterCollateralMaxRef.current = false;
    }, [isLoadingMaxBorrowLimit, isFetchingMaxBorrowLimit, availableBorrowLimit, borrowField]);

    const { increasePrincipalParams, submit: handleBorrowMoreSubmit } = useSubmitIncreasePrincipal({
        loanExpanded,
        option,
        additionalPrincipal,
        additionalPrincipalLimit,
        isRefinance
    });

    const reset = useCallback(() => {
        borrowField.reset();
        setHasTouchedCollateral(false);
        shouldMaxBorrowAfterCollateralMaxRef.current = false;
        _setCollateralInputAmount(undefined);
    }, [borrowField, _setCollateralInputAmount]);

    const onAddCollateralOnlySubmit = useCallback(async () => {
        const result = await handleAddCollateralSubmit();
        if (result?.isOk?.()) reset();
        return result;
    }, [handleAddCollateralSubmit, reset]);

    const handleManualWindSubmit = useManualIncreaseLeverage({
        loanExpanded,
        increasePrincipalParams,
        collateralInputAmount,
        onSuccess: reset
    });

    const hasBorrowInput = !!additionalPrincipal && additionalPrincipal > 0;

    const borrowLimit = additionalPrincipalLimit?.additionalBorrowLimit;
    const isClampingBorrow =
        borrowLimit !== undefined && additionalPrincipal !== undefined && additionalPrincipal > borrowLimit;

    const setCollateralMax = useCallback(() => {
        setHasTouchedCollateral(true);
        shouldMaxBorrowAfterCollateralMaxRef.current = true;
        _setCollateralInputAmount(collateralBalance);
    }, [collateralBalance, _setCollateralInputAmount]);

    const borrowMoreWarning = useMemo(() => {
        if (!loanExpanded || isLoadingMaxBorrowLimit || isLoadingBestQuotes) return undefined;

        if (!validOptions?.length) return { message: "No offers for current duration", color: "info" as const };

        if (isClampingBorrow) return undefined;

        if (maxBorrowAlert) return { message: maxBorrowAlert, color: "warning" as const };
        return undefined;
    }, [
        loanExpanded,
        isLoadingMaxBorrowLimit,
        isLoadingBestQuotes,
        validOptions?.length,
        isClampingBorrow,
        maxBorrowAlert
    ]);

    const addCollateralWarning = useMemo(() => {
        if (!loanExpanded) return undefined;
        if (permissionDenied && permissionMessage) return { message: permissionMessage, color: "warning" as const };
        return undefined;
    }, [loanExpanded, permissionDenied, permissionMessage]);

    const ctaError = useMemo(() => {
        if (!loanExpanded) return "Loading";
        if (!collateralInputAmount && !hasBorrowInput) return "Enter amount";

        if (collateralInputAmount) {
            if (insufficientBalance) return "Insufficient balance";
            if (permissionDenied) return "Permission denied";
        }

        if (hasBorrowInput) {
            if (!option?.bestQuote) return "Loading quotes";
            if (
                !additionalPrincipalLimit?.additionalBorrowLimit &&
                additionalPrincipalLimit?.additionalBorrowLimit !== 0
            )
                return "Loading limit";

            if (isClampingBorrow) return undefined;

            if (additionalPrincipal && additionalPrincipal > (additionalPrincipalLimit?.additionalBorrowLimit ?? 0))
                return "Exceeds available";
        }

        return undefined;
    }, [
        loanExpanded,
        collateralInputAmount,
        hasBorrowInput,
        insufficientBalance,
        permissionDenied,
        option?.bestQuote,
        additionalPrincipal,
        additionalPrincipalLimit?.additionalBorrowLimit,
        isClampingBorrow
    ]);

    // ---- single submit handler ----
    const onSubmit = useCallback(() => {
        if (collateralInputAmount && hasBorrowInput) return handleManualWindSubmit();
        if (collateralInputAmount) return onAddCollateralOnlySubmit();
        return handleBorrowMoreSubmit();
    }, [
        collateralInputAmount,
        hasBorrowInput,
        handleManualWindSubmit,
        onAddCollateralOnlySubmit,
        handleBorrowMoreSubmit
    ]);

    const buttonProps = useMemo(
        () => ({
            disabled: !!ctaError,
            asyncCta: { onClickWithResult: onSubmit },
            transactionType:
                hasBorrowInput && collateralInputAmount
                    ? TransactionTypes.BorrowMore
                    : hasBorrowInput
                      ? TransactionTypes.BorrowMore
                      : TransactionTypes.Topup,
            children:
                ctaError ??
                (hasBorrowInput && collateralInputAmount
                    ? "Add Collateral & Borrow"
                    : hasBorrowInput
                      ? "Borrow more"
                      : "Add collateral")
        }),
        [ctaError, onSubmit, hasBorrowInput, collateralInputAmount]
    );

    return {
        collateral: {
            value: collateralInputAmount,
            setValue: setCollateralInputAmount,
            metadata: collateralMetadata,
            balance: collateralBalance,
            balanceLoading,
            insufficientBalance,
            setMax: setCollateralMax,
            warning: addCollateralWarning
        },
        borrow: {
            value: additionalPrincipal,
            setValue: borrowField.setValue,
            metadata: loanExpanded?.principalMetadata,
            available: availableBorrowLimit,
            loading: isLoadingMaxBorrowLimit || availableBorrowLimit === undefined,
            setMax: borrowField.setMax,
            warning: borrowMoreWarning
        },
        buttonProps
    };
}
